import { useMemo } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'

import { AutomationFormValues } from '~/components/automations/automationSchema'
import { useSelectedBankAccount } from '~/components/automations/useSelectedBankAccount'
import { SupportedChainId } from '~/constants/chains'
import { useMultipleTokensMetadata } from '~/hooks/useTokenMetadata'
import { EvmAddress } from '~/utils/address'

/**
 * Generates a suggested automation name based on the configured swaps.
 * e.g. "80/20 USDCe/wstETH" when swaps total 100%,
 * or "50/20/30 USDC/wstETH/as-is" when there is a 30% passthrough,
 * or "Swap to USDC" when a single token receives 100%.
 *
 * Returns the generated name and the user-entered name (if any).
 */
export const useAutomationName = () => {
  const { control } = useFormContext<AutomationFormValues>()
  const swaps = useWatch({ control, name: 'swaps' })
  const name = useWatch({ control, name: 'name' })
  const selectedBankAccount = useSelectedBankAccount()

  const tokenInputs = useMemo(
    () =>
      swaps.map((swap) => ({
        chainId: swap.targetChainId as SupportedChainId,
        address: EvmAddress.format(swap.targetTokenAddress),
      })),
    [swaps],
  )

  const tokensMetadata = useMultipleTokensMetadata(tokenInputs)

  const generatedName = useMemo(() => {
    if (selectedBankAccount) return 'USD offramp'

    if (swaps.length === 0) return ''

    const percentages = swaps.map((s) => s.percent)
    const symbols = tokensMetadata.map((t) => t.symbol)

    if (symbols.length === 0) return ''

    const passThroughPercent = 100 - percentages.reduce((sum, p) => sum + p, 0)

    if (passThroughPercent > 0) {
      percentages.push(passThroughPercent)
      symbols.push('as-is')
    }

    if (percentages.length === 1 && percentages[0] === 100)
      return `Swap to ${symbols[0]}`

    return `${percentages.join('/')} ${symbols.join('/')}`
  }, [selectedBankAccount, swaps, tokensMetadata])

  return { generatedName, name }
}
